import { Location, NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseFormComponent } from 'components/form/base-form';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { ServiceFormActionsComponent } from 'components/service/service-form-actions/service-form-actions.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { TopicInfoComponent } from 'components/topic-info/topic-info.component';
import { ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import { ResearchTopicByClientIdCard, ResearchTopicListItem } from 'types/client.interface';
import {
  BackofficeReadingRoomSubmissionCard,
  ReadingRoomSubmissionResearchTopicItem,
  UpdateDraftReadingRoomSubmissionRequest
} from 'types/service.interface';

export const VISIT_ROOM_TOPIC_ADD = 'visitRoomTopicForm';
export const VISIT_ROOM_TOPIC_ID = 'visitRoomTopicId';
export const TOPICS_SHOWN = 5;

@Component({
  selector: 'app-visit-room-topic-form',
  standalone: true,
  imports: [
    TooltipComponent,
    TranslateModule,
    NgClass,
    NgbCollapse,
    ServiceFormActionsComponent,
    TopicInfoComponent,
    ConfirmModalComponent
  ],
  templateUrl: './visit-room-topic-form.component.html',
  styleUrls: ['./visit-room-topic-form.component.scss', '../../../form/base-form.scss']
})
export class VisitRoomTopicFormComponent extends BaseFormComponent implements OnInit {
  public readonly TOPICS_SHOWN = TOPICS_SHOWN;
  @Input() application!: any;
  @Input() researchTopics: ResearchTopicByClientIdCard[] = [];
  @Output()
  applicationUpdated: EventEmitter<BackofficeReadingRoomSubmissionCard> = new EventEmitter<BackofficeReadingRoomSubmissionCard>();
  @Output() saveDraft: EventEmitter<UpdateDraftReadingRoomSubmissionRequest> = new EventEmitter<UpdateDraftReadingRoomSubmissionRequest>();
  @Output() stepChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() formChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  researchTopicsCollapsed: boolean[] = [];
  chosenTopics: string[] = [];
  topicsShown: number = TOPICS_SHOWN;
  isOpenConfirmDeleteSubmission = false;
  isFormChanged = false;

  constructor(
    protected override location: Location,
    protected override router: Router,
    protected override formBuilder: FormBuilder,
    protected override apiService: ApiService,
    protected authService: AuthorizationService,
    protected override toastService: ToastService,
    protected override translateService: TranslateService
  ) {
    super(location, router, formBuilder, apiService, toastService, translateService);
    this.fileUploadComponentCount = 1;
  }

  override ngOnInit(): void {
    const storedTopics = localStorage.getItem(VISIT_ROOM_TOPIC_ID);

    if (localStorage.getItem(VISIT_ROOM_TOPIC_ID)) {
      try {
        this.chosenTopics = storedTopics ? JSON.parse(storedTopics) : [];
        localStorage.removeItem(VISIT_ROOM_TOPIC_ID);
      } catch (error) {
        console.error('Error parsing chosenTopics from localStorage:', error);
        this.chosenTopics = [];
      }
    }

    this.application.researchTopics.forEach((topic: ReadingRoomSubmissionResearchTopicItem) => {
      if (!this.chosenTopics.includes(topic.id!.toString())) {
        this.chosenTopics.push(topic.id!.toString());
      }
    });

    if (!Array.isArray(this.chosenTopics)) {
      this.chosenTopics = [];
      this.application.researchTopics.forEach((topic: ReadingRoomSubmissionResearchTopicItem) => {
        if (!this.chosenTopics.includes(topic.id!.toString())) {
          this.chosenTopics.push(topic.id!.toString());
        }
      });
    }

    this.filterResearchTopics();

    if (this.researchTopics.length === 0) {
      this.addTopic();
    }

    this.researchTopics.forEach(() => {
      this.researchTopicsCollapsed.push(true);
    });

    this.sortResearchTopics();
    this.formChanged.emit(false);
  }

  filterResearchTopics(): void {
    this.researchTopics = this.researchTopics.filter((topic) => topic.isActive);
  }

  sortResearchTopics(): void {
    this.researchTopics.sort((a: ResearchTopicByClientIdCard, b: ResearchTopicByClientIdCard) => {
      const isAChosen = this.chosenTopics.includes(a.id!);
      const isBChosen = this.chosenTopics.includes(b.id!);

      return isAChosen && isBChosen ? 0 : isAChosen ? -1 : 1; // Chosen topics come first
    });
  }

  override fillForm(): void {
    //
  }

  updateTopics(topic: ResearchTopicListItem): void {
    this.formChanged.emit(true);

    if (!this.chosenTopics.includes(topic.id!)) {
      this.chosenTopics.push(topic.id!);
    } else {
      this.chosenTopics = this.chosenTopics.filter((id) => id !== topic.id);
    }

    localStorage.setItem(VISIT_ROOM_TOPIC_ID + this.application?.id, JSON.stringify(this.chosenTopics));
  }

  override back(confirmed: boolean = false): void {
    this.checkIsFormChanged();

    if (this.isFormChanged && !confirmed) {
      this.isOpenConfirmBack = true;
    } else {
      localStorage.removeItem(VISIT_ROOM_TOPIC_ID);

      this.router.navigate([], {
        queryParams: { solis: 1 },
        queryParamsHandling: 'merge'
      });
    }
  }

  override submit(navigate: boolean = true): void {
    if (this.chosenTopics.length === 0) {
      this.toastService.showDanger('noTopicsSelected', '', {
        shouldTranslate: true
      });
    } else {
      if (this.application.initializationStatus === 'Created') {
        this.apiService
          .updateVisitRoomTopics({
            readingRoomSubmissionId: this.application.id,
            researchTopicIds: this.chosenTopics as any
          })
          .subscribe(
            (app: BackofficeReadingRoomSubmissionCard) => {
              if (navigate) {
                this.applicationUpdated.emit(app);
                this.router.navigate([], {
                  queryParams: { solis: 3 },
                  queryParamsHandling: 'merge'
                });
              }
            },
            (error: any) => {
              console.error(error);
              this.toastService.showRequestError();
            }
          );
      } else {
        this.handleSaveDraft(true);
      }
    }
  }

  handleSaveDraft(nextStep: boolean = false): void {
    if (this.application) {
      const args: UpdateDraftReadingRoomSubmissionRequest = {
        readingRoomSubmissionId: this.application.id,
        ...this.form.value,
        clientId: this.application.client.id,
        readerCardId: this.application.readerCard.id,
        readingRoomId: this.application.readingRoom.id,
        visitDate: this.application.visitDate,
        visitTimeFrom: this.application.visitTimeFrom,
        visitTimeTo: this.application.visitTimeTo,
        visitInformation: this.application.visitInformation,
        researchTopicIds: this.chosenTopics || [],
        agreementToReadingRoomTerms: this.application.agreementToReadingRoomTerms,
        hasRequestedDocuments: this.application.hasRequestedDocuments,
        requestedDocumentsInformation: this.application.requestedDocumentsInformation,
        requestedDocuments: this.application.requestedDocuments || []
      };

      this.saveDraft.emit(args);

      if (nextStep) {
        this.router.navigate([], {
          queryParams: { solis: 3 },
          queryParamsHandling: 'merge'
        });
      }
    }
  }

  addTopic(): void {
    localStorage.setItem(VISIT_ROOM_TOPIC_ID, JSON.stringify(this.chosenTopics));
    localStorage.setItem(VISIT_ROOM_TOPIC_ADD, this.application.id.toString());
    this.router.navigate([`/petijuma-tema/pievienot/${this.application.client.id}`]);
  }

  editTopic(topicId: string): void {
    localStorage.setItem(VISIT_ROOM_TOPIC_ID, JSON.stringify(this.chosenTopics));
    localStorage.setItem(VISIT_ROOM_TOPIC_ADD, this.application.id.toString());
    this.router.navigate([`/petijuma-tema/rediget/${topicId}/${this.application.client.id}`]);
  }

  onModalClose(): void {
    this.isOpenConfirmDeleteSubmission = false;
  }

  handleDeleteDraft() {
    this.isOpenConfirmDeleteSubmission = true;
  }

  deleteReadingRoomSubmission() {
    if (this.application?.id) {
      this.apiService.deleteReadingRoomSubmission(this.application?.id).subscribe(
        () => {
          this.router.navigate(['/lasitavas-apmeklejums']);
          this.toastService.showSuccess('applicationDraftDeleted', '', {
            shouldTranslate: true
          });
        },
        (error: any) => {
          console.error(error);
          this.toastService.showRequestError();
        }
      );
    }
  }

  checkIsFormChanged(): void {
    const appTopicIds = this.application.researchTopics?.map((topic: ReadingRoomSubmissionResearchTopicItem) => topic.id);
    let currentCheck = false;

    this.chosenTopics.forEach((topicId: string, index: number) => {
      if (!appTopicIds?.includes(topicId)) {
        this.isFormChanged = true;
        currentCheck = true;
      }

      if (!currentCheck && index === this.chosenTopics.length - 1) {
        this.isFormChanged = false;
      }
    });

    appTopicIds?.forEach((topicId: string, index: number) => {
      if (!this.chosenTopics.includes(topicId)) {
        this.isFormChanged = true;
        currentCheck = true;
      }

      if (!currentCheck && index === appTopicIds.length - 1) {
        this.isFormChanged = false;
      }
    });
  }

  hasExpiredFiles(researchTopic: ResearchTopicByClientIdCard): boolean {
    const today = new Date();

    const allFiles = [...(researchTopic.topicFiles || []), ...(researchTopic.justificationFiles || [])];

    return allFiles.some((file) => {
      const note = file.fileNote;
      if (note?.termType === 'Fixed' && note.validTill) {
        const validTillDate = new Date(note.validTill);
        return validTillDate < today;
      }
      return false;
    });
  }
}
